import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Skeleton } from '@mui/material';
import { FaPlus, FaSave, FaSpinner } from 'react-icons/fa';
import { useSelector } from 'react-redux';
import SalesOrderReservationModal from '../modals/SalesOrderReservationModal'
import SalesOrderCancelReservationModal from '../modals/SalesOrderCancelReservationModal'
import StatusBadge from 'ui-component/StatusBadge';
import { getCurrencySymbolV2, numberFormatter } from 'utils/currency';
import { CommonDateOnlyFormat } from 'helpers/common.helper';
import usePermissions from 'hooks/usePermissions';
import useIsMobile from 'utils/isMobileUtils';
import DeleteConfirmation from 'ui-component/confirmation-dialogs/deleteConfirmation/DeleteConfirmation';
import { toast } from 'react-hot-toast';


// ✅ Clear
const ProductOverviewComponent = ({ details, isPageLoading, viewedBy = 'ADMIN', onHandleLoadSalesOrder }) => {
    const [allPermissions, hasEveryPermission] = usePermissions();
    const history = useNavigate();
    const isMobile = useIsMobile();
    
    const selectedCurrency = useSelector((state) => state.main.selectedCurrency);
    const [defaultCurrency, setDefaultCurrency] = useState(details?.invoice?.currency || 'usd');

    const [showReserveModal, setShowReserveModal] = useState(false);
    const [showRestoreModal, setShowRestoreModal] = useState(false);
    const [showCancelReservationModal, setShowCancelReservationModal] = useState(false);
    const [showVoidBackorderModal, setShowVoidBackorderModal] = useState(false);
    const [selectedProductId, setSelectedProductId] = useState(null);
    const [selectedColorId, setSelectedColorId] = useState(null);
    const [selectedProductQty, setSelectedProductQty] = useState(0);
    const [selectedProductName, setSelectedProductName] = useState('');
    const [selectedColorName, setSelectedColorName] = useState('');
    const [selectedBackOrderId, setSelectedBackOrderId] = useState(null);
    const [isCancelBackorderLoading, setIsCancelBackorderLoading] = useState(false);
    const [isRestoreBackorderLoading, setIsRestoreBackorderLoading] = useState(false);

    
    const handleOpenNewTab = (pageModule, id, ref = null) => {
        let url = '';
        if (pageModule == 'products') {
            url = viewedBy === 'ADMIN' ? `/products/${id}` : `/client/products/${id}`;
        } else if (pageModule == 'contacts') {
            url = `/contacts/${id}`;
        } else if (pageModule == 'purchase_orders') {
            url = `/purchasing/view/${id}`;
        } else if (pageModule == 'sales_orders') {
            url = viewedBy === 'ADMIN' ? `/sales/sales-orders/${id}?ref=${ref}` : `/client/sales-orders/${id}?ref=${ref}`;
        } else if (pageModule == 'invoices') {
            url = viewedBy === 'ADMIN' ? `/sales/invoices/${id}?ref=${ref}` : `/client/invoices/${id}?ref=${ref}`;
        } else if (pageModule == 'quotations') {
            url = viewedBy === 'ADMIN' ? `/sales/quotations/${id}?ref=${ref}` : `/client/quotations/${id}?ref=${ref}`;
        }
        if (url) {
            history(url);
        }
    };
    const handleClose = () => {
        setShowReserveModal(false)
        setShowCancelReservationModal(false)
        setSelectedProductId(null);
        setSelectedColorId(null);
        setSelectedProductQty(0);
        setSelectedProductName('');
        setSelectedColorName('');
        setSelectedBackOrderId(null);
    }

    const handleVoidBackOrder = () => {
        setIsCancelBackorderLoading(true);
        toast.promise(
            api
                .voidBackOrder(selectedBackOrderId)
                .then(
                    (res) => {
                        if (res.status) {

                            setSelectedProductId(null);
                            setSelectedColorId(null);
                            setSelectedProductQty(0);
                            setSelectedProductName('');
                            setSelectedColorName('');
                            setSelectedBackOrderId(null);
                            onHandleLoadSalesOrder()
                            setIsCancelBackorderLoading(false);
                        }

                        return res?.status ? res : null;
                    },
                    () => { }
                ),
            {
                loading: 'Cancelling Backorder...',
                success: (data) => `${data.message}`,
                error: <b>Failed to cancel backorder</b>
            }
        );
    }
    const handleRestoreBackOrder = () => {
        setIsRestoreBackorderLoading(true);
        toast.promise(
            api
                .restoreBackOrder(selectedBackOrderId)
                .then(
                    (res) => {
                        if (res.status) {

                            setSelectedProductId(null);
                            setSelectedColorId(null);
                            setSelectedProductQty(0);
                            setSelectedProductName('');
                            setSelectedColorName('');
                            setSelectedBackOrderId(null);
                            setIsRestoreBackorderLoading(false);
                            onHandleLoadSalesOrder()
                        }

                        return res?.status ? res : null;
                    },
                    () => { }
                ),
            {
                loading: 'Restoring Backorder...',
                success: (data) => `${data.message}`,
                error: <b>Failed to restore backorder</b>
            }
        );
    }

    return (
        <>
            {!isMobile ? (
                <div style={{ padding: '0px 20px 20px 16px' }}>
            
                    <div className="row">
                        <div className="col-12">
                            <h2 className="d-flex font600" style={{ fontSize: '18px' }}>
                                {
                                    isPageLoading ? (
                                        <div style={{ width: '100px', marginBottom: '3px' }}>
                                            <Skeleton variant="rounded" width={'100%'} height={21}></Skeleton>
                                        </div>
                                    ) : (
                                        'Product Overview'
                                    )
                                }
                                
                            </h2>
                        </div>
                    </div>

                    <div className="row" style={{ marginTop: '-5px'}}>
                        <div className="col-12" style={{ padding: '0' }}>

                            <table className="table  min-width-unset tbl-space pm-table d-none d-md-table">
                                <thead>
                                    <tr className="sticky-header">
                                        <th scope="col" width="">Product</th>
                                        <th scope="col" width="">Back Orders</th>
                                        <th scope="col" width="">Reserved</th>
                                        <th scope="col" width=""></th>
                                    </tr>
                                </thead>
                                <tbody> 
                                    {
                                        details?.product_overview?.length > 0 && (
                                            details?.product_overview?.map((overview, index) => (
                                                <tr
                                                    key={index}
                                                    style={{ height: '50px' }}
                                                >
                                                    <td>
                                                        <div className="flex gap-2 align-items-center cursor-pointer">
                                                            <div
                                                                className="text-primary"
                                                                onClick={() => {
                                                                    handleOpenNewTab('products', overview?.product_id, null);
                                                                }}
                                                            >
                                                                <img src={`${overview?.thumbnail_url}`} alt="product" style={{width: '23px', height: '35px'}} />
                                                            </div>
                                                            <div>
                                                                <a href={`/products/${overview?.product_id}`} target="_blank" rel="noreferrer" style={{ textDecoration: 'none' }}>
                                                                    {overview?.product_stock_comparison?.product_name} {overview?.color}
                                                                </a>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td
                                                        className="cursor-pointer"
                                                        onClick={() => {
                                                            handleOpenNewTab('products', overview?.product_id, null);
                                                        }}
                                                    >
                                                            {
                                                                overview?.status === 'CANCELLED' ? (
                                                                    <span className={`text-gray-700`}>
                                                                        0 pc
                                                                    </span>
                                                                ) : (
                                                                    <span className={`text-danger`}>
                                                                        {overview?.product_stock_comparison?.backorder_qty}{' '}
                                                                        {parseInt(overview?.product_stock_comparison?.backorder_qty) > 1 ? 'pcs' : 'pc'}
                                                                    </span>
                                                                )
                                                            }
                                                    </td>
                                                    <td
                                                        className="cursor-pointer"
                                                        onClick={() => {
                                                            handleOpenNewTab('products', overview?.product_id, null);
                                                        }}
                                                    >
                                                        <span className={`${parseInt(overview?.product_stock_comparison?.reserved_qty) > 0 ? 'text-green-800' : 'text-gray-700'}`}>
                                                            {overview?.product_stock_comparison?.reserved_qty}{' '}
                                                            {parseInt(overview?.product_stock_comparison?.reserved_qty) > 1 ? 'pcs' : 'pc'}
                                                        </span>
                                                        
                                                    </td>
                                                    <td style={{ width: '165px' }}>
                                                        <div className="flex gap-2 justify-content-end align-items-center">
                                                            {
                                                                overview?.status === 'CANCELLED' ? (
                                                                    <>
                                                                        <div style={{ width: '30px', marginRight: '40px' }}>
                                                                            <StatusBadge itemStatus={'Cancelled'} />
                                                                        </div>
                                                                        {
                                                                            parseInt(overview?.product_stock_comparison?.backorder_qty) > 0 && (

                                                                                <button
                                                                                    className="btn btn-primary btn-sm btn-outlined-main white-var btn-icon-only btn-icon-square"
                                                                                    // disabled={!dataLoaded}
                                                                                    onClick={() => {
                                                                                        if (!isRestoreBackorderLoading) {
                                                                                            setShowRestoreModal(true);
                                                                                            setSelectedProductId(overview?.product_id);
                                                                                            setSelectedColorId(overview?.color_id);
                                                                                            setSelectedProductQty(parseInt(overview?.product_stock_comparison?.backorder_qty))
                                                                                            setSelectedProductName(overview?.product_stock_comparison?.product_name);
                                                                                            setSelectedColorName(overview?.color);
                                                                                            setSelectedBackOrderId(overview?.backorder_id);
                                                                                        }
                                                                                    }}
                                                                                >
                                                                                    
                                                                                    {isRestoreBackorderLoading ? (
                                                                                        <>
                                                                                            <FaSpinner className="spinner" style={{ height: '15px', width: '25px', marginRight: '0px' }} /> Restoring Backorder
                                                                                        </>
                                                                                    ) : (
                                                                                        'Restore Backorder'
                                                                                    )}
                                                                                        
                                                                                </button>
                                                                            )
                                                                        }
                                                                    </>
                                                                ): (
                                                                    <>
                                                                        {
                                                                            parseInt(overview?.product_stock_comparison?.reserved_qty) > 0 ? (
                                                                                <button
                                                                                    className="btn btn-danger"
                                                                                    // disabled={!dataLoaded}
                                                                                    onClick={() => {
                                                                                        setShowCancelReservationModal(true);
                                                                                        setSelectedProductId(overview?.product_id);
                                                                                        setSelectedColorId(overview?.color_id);
                                                                                        setSelectedProductQty(parseInt(overview?.product_stock_comparison?.reserved_qty))
                                                                                        setSelectedProductName(overview?.product_stock_comparison?.product_name);
                                                                                        setSelectedColorName(overview?.color);
                                                                                        setSelectedBackOrderId(overview?.backorder_id);
                                                                                    }}
                                                                                >
                                                                                    Cancel
                                                                                </button>
                                                                            ) : (
                                                                                 overview?.status === 'PENDING' && (
                                                                                    <button
                                                                                        className="btn btn-danger"
                                                                                        // disabled={!dataLoaded}
                                                                                        onClick={() => {
                                                                                            if (!isCancelBackorderLoading) {
                                                                                                setShowVoidBackorderModal(true);
                                                                                                setSelectedProductId(overview?.product_id);
                                                                                                setSelectedColorId(overview?.color_id);
                                                                                                setSelectedProductQty(parseInt(overview?.product_stock_comparison?.backorder_qty))
                                                                                                setSelectedProductName(overview?.product_stock_comparison?.product_name);
                                                                                                setSelectedColorName(overview?.color);
                                                                                                setSelectedBackOrderId(overview?.backorder_id);
                                                                                            }
                                                                                        }}
                                                                                    >
                                                                                        
                                                                                        {isCancelBackorderLoading ? (
                                                                                            <>
                                                                                                <FaSpinner className="spinner" style={{ height: '15px', width: '25px', marginRight: '0px' }} /> Cancelling Backorder
                                                                                            </>
                                                                                        ) : (
                                                                                            'Cancel Backorder'
                                                                                        )}
                                                                                        
                                                                                    </button>
                                                                                )       
                                                                            )
                                                                        }
                                                                        {/* {
                                                                            overview?.status === 'PENDING' && (
                                                                                <button
                                                                                    className="btn btn-danger"
                                                                                    // disabled={!dataLoaded}
                                                                                    onClick={() => {
                                                                                        setShowVoidBackorderModal(true);
                                                                                        setSelectedProductId(overview?.product_id);
                                                                                        setSelectedColorId(overview?.color_id);
                                                                                        setSelectedProductQty(parseInt(overview?.product_stock_comparison?.backorder_qty))
                                                                                        setSelectedProductName(overview?.product_stock_comparison?.product_name);
                                                                                        setSelectedColorName(overview?.color);
                                                                                        setSelectedBackOrderId(overview?.backorder_id);
                                                                                    }}
                                                                                >
                                                                                    Void
                                                                                </button>
                                                                            )
                                                                        } */}
                                                                        {
                                                                            parseInt(overview?.product_stock_comparison?.backorder_qty) > 0 && (

                                                                                <button
                                                                                    className="btn btn-primary btn-sm btn-outlined-main white-var btn-icon-only btn-icon-square"
                                                                                    // disabled={!dataLoaded}
                                                                                    onClick={() => {
                                                                                        setShowReserveModal(true);
                                                                                        setSelectedProductId(overview?.product_id);
                                                                                        setSelectedColorId(overview?.color_id);
                                                                                        setSelectedProductQty(parseInt(overview?.product_stock_comparison?.backorder_qty))
                                                                                        setSelectedProductName(overview?.product_stock_comparison?.product_name);
                                                                                        setSelectedColorName(overview?.color);
                                                                                        setSelectedBackOrderId(overview?.backorder_id);
                                                                                    }}
                                                                                >
                                                                                    Reserve
                                                                                </button>
                                                                            )
                                                                        }
                                                                    </>
                                                                )
                                                            }
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))
                                        )
                                    }
                                </tbody>
                            </table>
                        </div>
                    </div>

                </div>
            ) : (
                // --- MOBILE VERSION FROM ESTIMATION ---
                <div
                    style={{ display: 'flex', flexDirection: 'column' }}
                    onClick={() => {
                        if (viewedBy === 'ADMIN') {
                            handleOpenNewTab('invoices', details?.invoice?.id, details?.invoice?.ref);
                        } else {
                            handleOpenNewTab('invoices', details?.invoice?.invoice_number, details?.invoice?.ref);
                        }
                    }}
                >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px' }}>
                        <h2 className="d-flex font500" style={{ fontSize: '17px', marginBottom: 0 }}>
                            Invoice
                        </h2>
                        <div>
                            <div style={{ width: '90px' }}>
                                <StatusBadge itemStatus={details?.invoice?.status} />
                            </div>
                        </div>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ display: 'flex', gap: 5, alignItems: 'center', fontWeight: 500, fontSize: 13 }}>
                            Invoice #:
                            <div
                                className={`text-primary cursor-pointer`}
                                onClick={() => {
                                    if (viewedBy === 'ADMIN') {
                                        handleOpenNewTab('invoices', details?.invoice?.id, details?.invoice?.ref);
                                    } else {
                                        handleOpenNewTab('invoices', details?.invoice?.invoice_number, details?.invoice?.ref);
                                    }
                                }}
                            >
                                {details?.invoice?.invoice_number}
                            </div>
                        </div>
                        <div>
                            {moment(details?.invoice?.date).format(CommonDateOnlyFormat)}
                        </div>
                    </div>
                    {
                        viewedBy === 'ADMIN' && (
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontWeight: 500, fontSize: 13 }}>
                                <div
                                    className={`${hasEveryPermission(['View Contact Details (Page)']) ? 'text-primary cursor-pointer' : ''}`}
                                    onClick={() => {
                                        if (hasEveryPermission(['View Contact Details (Page)'])) {
                                            handleOpenNewTab('contacts', details?.invoice?.customer?.id, details?.invoice?.ref);
                                        }
                                    }}
                                >
                                    {details?.invoice?.customer?.name}
                                </div>
                                <div style={{ fontWeight: 500 }}>
                                    <div className="flex gap-1 w-full">
                                        
                                        <div style={{ marginTop: `${selectedCurrency === 'aed' ? '-1px' : '0'}` }}>
                                            {getCurrencySymbolV2(selectedCurrency, details?.invoice?.currency, '11px', '11px')}
                                        </div>
                                        <div>
                                            {details?.invoice?.converted_currency_total ? numberFormatter.format(details?.invoice?.converted_currency_total[selectedCurrency === 'all' ? defaultCurrency : selectedCurrency]) : 'N/A'}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )
                    }
                </div>
            )}
            {
                showReserveModal && (
					<SalesOrderReservationModal
                        openPopup={showReserveModal}
                        salesOrderId={details?.id}
                        name={selectedProductName}
                        productId={selectedProductId}
                        colorId={selectedColorId}
                        colorName={selectedColorName}
                        quantity={selectedProductQty}
                        backOrderId={selectedBackOrderId}
                        handleClose={() => {
                            handleClose()
                        }}
                        handleLoadSalesOrder={() => {
                            onHandleLoadSalesOrder()
                            handleClose()
                        }}
					/>
                )   
            }
            {
                showCancelReservationModal && (
					<SalesOrderCancelReservationModal
                        openPopup={showCancelReservationModal}
                        salesOrderId={details?.id}
                        name={selectedProductName}
                        productId={selectedProductId}
                        colorId={selectedColorId}
                        colorName={selectedColorName}
                        quantity={selectedProductQty}
                        backOrderId={selectedBackOrderId}
                        handleClose={() => {
                            handleClose()
                        }}
                        handleLoadSalesOrder={() => {
                            onHandleLoadSalesOrder()
                            handleClose()
                        }}
					/>
                )   
            }

            {showVoidBackorderModal && (
                <DeleteConfirmation
                    show={showVoidBackorderModal}
                    handleClose={() => {
                        setSelectedProductId(null);
                        setSelectedColorId(null);
                        setSelectedProductQty(0);
                        setSelectedProductName('');
                        setSelectedColorName('');
                        setSelectedBackOrderId(null);
                        setShowVoidBackorderModal(false);
                    }}
                    handleDelete={() => {
                        setShowVoidBackorderModal(false);
                        handleVoidBackOrder()
                    }}
                    title="Confirm"
                    message={`Are you sure you want to cancel ${selectedProductQty} pcs of ${selectedProductName}${selectedColorName ? ` ${selectedColorName}?` : '?'}`}
                    customDeleteButtonText={'Yes, Cancel Backorder'}
                />
            )}
            {showRestoreModal && (
                <DeleteConfirmation
                    show={showRestoreModal}
                    handleClose={() => {
                        setSelectedProductId(null);
                        setSelectedColorId(null);
                        setSelectedProductQty(0);
                        setSelectedProductName('');
                        setSelectedColorName('');
                        setSelectedBackOrderId(null);
                        setShowRestoreModal(false);
                    }}
                    handleDelete={() => {
                        setShowRestoreModal(false);
                        handleRestoreBackOrder()
                    }}
                    title="Confirm"
                    message={`Are you sure you want to restore ${selectedProductQty} pcs of ${selectedProductName}${selectedColorName ? ` ${selectedColorName}?` : '?'}`}
                    customDeleteButtonText={'Yes, Restore Backorder'}
                />
            )}
        </>
    );
};

export default ProductOverviewComponent;
